import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOSchema from '@/components/base/SEOSchema';
import { testimonials } from '@/mocks/faqData';

const breadcrumbItems = [
  { label: '首页', href: '/' },
  { label: '关于我们' },
];

const milestones = [
  { year: '2020', title: '平台创立', desc: '虫虫漫画正式上线，专注韩国漫画领域，首批引进50部正版韩漫作品。' },
  { year: '2021', title: '快速成长', desc: '用户突破100万，漫画库存超过500部，成为国内领先的韩漫阅读平台。' },
  { year: '2022', title: '内容升级', desc: '与韩国TOP10漫画工作室达成战略合作，独家引进多部热门作品，翻译团队扩充至50人。' },
  { year: '2023', title: '体验革新', desc: '全新UI改版上线，推出沉浸式阅读模式，用户留存率提升40%，月活用户突破300万。' },
  { year: '2024', title: '生态扩展', desc: '上线会员体系和社区功能，漫画总量突破1000部，累计阅读量超过10亿次。' },
];

export default function AboutPage() {
  return (
    <>
      <SEOSchema type="Organization" />
      <SEOSchema type="WebSite" />
      <SEOSchema
        type="WebPage"
        data={{
          name: '关于我们 - 虫虫漫画',
          description: '了解虫虫漫画的品牌故事、发展历程和联系方式。虫虫漫画打造二次元动漫世界，专注于提供沉浸式韩国漫画体验。',
          url: window.location.origin + '/about',
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full max-w-5xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <section className="py-10">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4 text-center">
                关于虫虫漫画
              </h1>
              <p className="text-sm text-foreground-500 text-center max-w-2xl mx-auto mb-12">
                打造二次元动漫世界，提供沉浸式韩国漫画体验
              </p>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-16">
                <div>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-800 mb-4">
                    我们的故事
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    虫虫漫画诞生于2020年，由一群热爱二次元文化的年轻人共同创立。我们深知，对于漫画爱好者来说，阅读体验的重要性不亚于内容本身。因此，从平台创立之初，我们就将"沉浸式阅读"作为核心理念，致力于为每一位读者打造最纯粹的漫画世界。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    我们与韩国多家顶级漫画工作室建立了深度合作，确保每一部作品都获得正版授权。同时，我们组建了一支专业的翻译团队，所有译者均经过严格筛选和培训，力求在准确传达原文的同时，保留韩语漫画独特的语言风格和文化韵味。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    截至目前，虫虫漫画已拥有超过1000部正版韩国漫画，涵盖恋爱、奇幻、热血、都市、悬疑等八大分类，累计服务超过500万读者。未来，我们将继续扩展内容库，优化阅读体验，让更多人感受到韩国漫画的魅力。
                  </p>
                </div>
                <div className="rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20warm%20cozy%20anime%20style%20illustration%20of%20a%20person%20reading%20comics%20on%20a%20comfortable%20sofa%20with%20soft%20lamp%20light%2C%20bookshelves%20in%20background%2C%20relaxing%20atmosphere%2C%20Korean%20manhwa%20aesthetic%2C%20warm%20coral%20and%20amber%20tones%2C%20clean%20composition&width=600&height=400&seq=about-brand&orientation=landscape"
                    alt="虫虫漫画品牌形象"
                    title="虫虫漫画 - 沉浸式韩国漫画阅读"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="mb-16">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-800 mb-8 text-center">
                  发展历程
                </h2>
                <div className="relative">
                  <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200/70 md:-translate-x-px"></div>
                  <div className="space-y-8">
                    {milestones.map((milestone, index) => (
                      <div
                        key={index}
                        className={`relative flex items-start gap-6 ${
                          index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                        }`}
                      >
                        <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-primary-500 -translate-x-1.5 mt-1.5 z-10 ring-4 ring-background-50"></div>
                        <div className={`pl-10 md:pl-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                          <span className="inline-block text-xs font-bold text-primary-500 bg-primary-50 px-3 py-1 rounded-full mb-2">
                            {milestone.year}
                          </span>
                          <h3 className="text-base font-heading font-bold text-foreground-800 mb-1">
                            {milestone.title}
                          </h3>
                          <p className="text-sm text-foreground-500 leading-relaxed">
                            {milestone.desc}
                          </p>
                        </div>
                        <div className="hidden md:block md:w-1/2"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mb-16" id="testimonials">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-800 mb-8 text-center">
                  读者评价
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {testimonials.map((t) => (
                    <div
                      key={t.id}
                      className="bg-background-100 rounded-lg p-5 border border-background-200/70"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <img
                          src={t.avatar}
                          alt={t.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div>
                          <p className="text-sm font-medium text-foreground-800">{t.name}</p>
                          <p className="text-xs text-foreground-400">{t.role}</p>
                        </div>
                        <div className="ml-auto flex items-center gap-0.5">
                          {Array.from({ length: t.rating }, (_, i) => (
                            <i key={i} className="ri-star-fill text-xs text-accent-500 w-3 h-3 flex items-center justify-center"></i>
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        "{t.content}"
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-10" id="contact">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-800 mb-8 text-center">
                  联系我们
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 mx-auto mb-3">
                      <i className="ri-mail-line text-xl text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <h3 className="text-sm font-heading font-bold text-foreground-800 mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-500">support@chongchongmanga.com</p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 mx-auto mb-3">
                      <i className="ri-phone-line text-xl text-accent-500 w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <h3 className="text-sm font-heading font-bold text-foreground-800 mb-1">客服热线</h3>
                    <p className="text-sm text-foreground-500">400-888-6666</p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-100 mx-auto mb-3">
                      <i className="ri-time-line text-xl text-secondary-500 w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <h3 className="text-sm font-heading font-bold text-foreground-800 mb-1">工作时间</h3>
                    <p className="text-sm text-foreground-500">周一至周日 9:00-21:00</p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}